// Code fetched on demand, fetched again when a fetch fails (spec
// docs/specs/editor/flaky-tests-1216.md): a phone between networks, or a CI runner whose network
// interfaces change, fails every fetch in flight, and the browser fetches a module again on its
// next import.

/** The longest wait between two tries. */
export const LOAD_AGAIN_MAX_MS = 8000;

/** The wait before the try after `failures` failures: half a second, twice as long each time. */
export function loadAgainDelayMs(failures: number): number {
  return Math.min(LOAD_AGAIN_MAX_MS, 500 * 2 ** Math.max(0, failures - 1));
}

/**
 * `load`'s answer, tried again after each failure (`loadAgainDelayMs`), and at once when the
 * browser is back online, until it answers; `signal` stops it, and the answer rejects with its
 * reason.
 */
export function loadAgain<T>(load: () => Promise<T>, signal: AbortSignal): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    let failures = 0;
    let timer = 0;
    const wait = () => {
      window.clearTimeout(timer);
      window.removeEventListener("online", attempt);
    };
    function attempt(): void {
      wait();
      if (signal.aborted) return;
      Promise.resolve()
        .then(load)
        .then(resolve, () => {
          if (signal.aborted) return;
          failures += 1;
          timer = window.setTimeout(attempt, loadAgainDelayMs(failures));
          window.addEventListener("online", attempt);
        });
    }
    if (signal.aborted) {
      reject(signal.reason);
      return;
    }
    signal.addEventListener(
      "abort",
      () => {
        wait();
        reject(signal.reason);
      },
      { once: true },
    );
    attempt();
  });
}
